import React, { useState, useEffect, useCallback } from 'react';
import { Button, StatusPill, Slideover, PageHeader, useToast, useConfirm } from '../ui';
import { PlusIcon, TrashIcon } from '@heroicons/react/20/solid';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import { Card } from '../ui/Card';
import { settingsAPI } from '../../services/api';
import CrewForm from './forms/CrewForm';
import { CREW_COLORS, resolveCrewColor } from '../../utils/crewColors';

/**
 * 20-preset closed palette per SET-3 / OB-6 — canonical source moved to
 * `utils/crewColors.js` in PT-22 W3 FP3-1 F3 so the same array + fallback
 * back both this Settings surface AND every picker/display swatch via
 * `resolveCrewColor`. Re-exported here for backward compat with CrewForm
 * (which imports `CREW_COLORS` from this module).
 */
export { CREW_COLORS };

/**
 * CrewsPage — Settings › Crews.
 *
 * Iter18 §2.4 rework to match SET-3 + OB-6 wireframe parity:
 *   • Editor field order per wire: color·name (inline) → description → color palette
 *     (6 shown + "More colors" expand → 20 total) → members type-ahead (+ adjacent
 *     "Invite techs" CTA) → specialties chips → active toggle.
 *   • Members type-ahead pulls from current tenant users list (settingsAPI.getUsers;
 *     getTenantUsers in a super-admin's dealer workspace, IDI-63 — see CrewForm).
 *   • Invite-techs modal per WFR-0710 fold: single Name field + Email (no First/Last).
 *   • List row shows description snippet next to crew name (WFR-0710).
 *   • NO crew-lead (constitutional; removed 2026-06-04 — DO NOT REBUILD).
 *
 * PT-22 W3-fp1 F1a: the AddCrew slideover body was extracted into
 * `./forms/CrewForm.js` so InlineCreate's modal can render the same form.
 * This page now only owns list rendering and add/edit orchestration; the
 * form owns its own state, invite modal, and footer buttons.
 */

export default function CrewsPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const [crews, setCrews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  // IDI-402: filter active/inactive crews
  const visibleCrews = showInactive
    ? crews
    : crews.filter((c) => c.active !== false);

  const fetchCrews = useCallback(async () => {
    try {
      const res = await settingsAPI.getList('crews');
      setCrews(Array.isArray(res) ? res : res?.items || []);
    } catch {
      /* empty state is fine */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCrews();
  }, [fetchCrews]);

  function openAdd() {
    setEditing(null);
    setDrawerOpen(true);
  }

  function openEdit(crew) {
    setEditing(crew);
    setDrawerOpen(true);
  }

  async function handleSaveCrew(payload) {
    if (editing) {
      await settingsAPI.updateListItem('crews', editing.id, payload);
      toast.success('Crew updated');
    } else {
      await settingsAPI.addListItem('crews', payload);
      toast.success('Crew created');
    }
    setDrawerOpen(false);
    fetchCrews();
  }

  async function handleDelete(crew) {
    const ok = await confirm({
      title: 'Delete crew?',
      message: `Delete crew "${crew.name}"?`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await settingsAPI.deleteListItem('crews', crew.id);
      toast.success('Crew deleted');
      fetchCrews();
    } catch (err) {
      toast.error('Failed to delete', { description: err?.message });
    }
  }

  return (
    <div className="px-6 space-y-6">
      <PageHeader
        title="Crews"
        subtitle="Manage field crews and team assignments."
        actions={<Button onClick={openAdd}><PlusIcon className="h-4 w-4 -ml-0.5 mr-1" />Add</Button>}
      />

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-14 rounded animate-pulse" style={{ backgroundColor: 'var(--neutral-100)' }} />
          ))}
        </div>
      ) : visibleCrews.length === 0 ? (
        <Card className="text-center py-12">
          <UserGroupIcon className="mx-auto h-10 w-10" style={{ color: 'var(--color-text-muted)' }} />
          <p className="mt-2 text-sm font-medium" style={{ color: 'var(--color-text-default)' }}>No crews yet</p>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            Create crews to organize your field teams.
          </p>
          <div className="mt-4">
            <Button variant="secondary" size="sm" onClick={openAdd}><PlusIcon className="h-4 w-4 -ml-0.5 mr-1" />Add</Button>
          </div>
        </Card>
      ) : (
        <>
          {/* IDI-402: inactive filter toggle */}
          {(() => {
            const inactiveCount = crews.filter((c) => c.active === false).length;
            return inactiveCount > 0 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowInactive(!showInactive)}
                  className="text-xs px-2.5 py-1 rounded-full border transition-colors"
                  style={{
                    borderColor: showInactive ? 'var(--color-action-primary)' : 'var(--color-border)',
                    color: showInactive ? 'var(--color-action-primary)' : 'var(--color-text-muted)',
                    backgroundColor: showInactive ? 'var(--color-action-primary-light, rgba(215,3,33,0.06))' : 'transparent',
                  }}
                >
                  {showInactive ? 'Hide' : 'Show'} inactive ({inactiveCount})
                </button>
              </div>
            );
          })()}
          <Card className="divide-y overflow-hidden">
          {visibleCrews.map((crew) => (
            <div
              key={crew.id}
              className="flex items-center gap-3 px-4 py-3 hover:bg-[color:var(--neutral-50)] cursor-pointer"
              onClick={() => openEdit(crew)}
            >
              <span
                className="h-5 w-5 rounded-full flex-shrink-0 border"
                style={{
                  backgroundColor: resolveCrewColor(crew),
                  borderColor: 'var(--color-border)',
                }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: 'var(--color-text-default)' }}>
                    {crew.name}
                  </p>
                  {crew.description && (
                    <p
                      className="text-xs truncate"
                      style={{ color: 'var(--color-text-muted)' }}
                      title={crew.description}
                    >
                      — {crew.description}
                    </p>
                  )}
                </div>
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  {(crew.members || []).length > 0
                    ? <>{(crew.members || []).length} member{(crew.members || []).length !== 1 ? 's' : ''}</>
                    : 'No members assigned'
                  }
                  {(crew.pending_invites || []).length > 0 && (
                    <> · {crew.pending_invites.length} pending</>
                  )}
                  {(crew.placeholders || []).length > 0 && (
                    <> · {crew.placeholders.length} placeholder{crew.placeholders.length !== 1 ? 's' : ''}</>
                  )}
                </p>
              </div>
              <StatusPill status={crew.active !== false ? 'active' : 'inactive'} size="sm" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleDelete(crew); }}
                className="p-1 rounded hover:bg-[color:var(--neutral-100)]"
                aria-label={`Delete ${crew.name}`}
              >
                <TrashIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
              </button>
            </div>
          ))}
        </Card>
        </>
      )}

      {/* Add/Edit Slideover — field order per SET-3 / OB-6 wire (renders CrewForm) */}
      <Slideover
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editing ? 'Edit Crew' : 'Add Crew'}
        /* PT-22 W3-fp1 F1a: CrewForm renders its own footer buttons so it
           drops into either a Slideover (footer=null) or a Modal identically. */
        footer={null}
      >
        <CrewForm
          key={editing?.id || 'new'}
          value={editing}
          onSave={handleSaveCrew}
          onCancel={() => setDrawerOpen(false)}
        />
      </Slideover>
    </div>
  );
}
